// ConvoAIPanel.tsx
import React, { useState, useRef, useEffect } from "react";
import ChatNeutral from '../../../assets/images/chat-neutral.png'

export interface ConvoAIMessage {
    role: "user" | "ai";
    text: string;
}

export interface ConvoAIPanelProps {
    isOpen: boolean;
    onClose: () => void;
    placeholder?: string;
    greeting?: string;
    onSend?: (message: string, history: ConvoAIMessage[]) => Promise<string> | string;
    className?: string;
    quickActions?: string[];
}

const ConvoAIPanel: React.FC<ConvoAIPanelProps> = ({
    isOpen,
    onClose,
    placeholder = "Ask anything...",
    greeting = "Describe your campaign and I'll auto-fill everything. Your audience, channels, timing, and the message.",
    onSend,
    className = "",
    quickActions = ["Adjust the Tone", "Change promo", "Edit URL", "Add Emojis", "Make it shorter", "Make it more formal"],
}) => {
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState<ConvoAIMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = async (customMessage?: string) => {
        const messageToSend = customMessage || input.trim();
        if (!messageToSend || loading) return;

        const userMsg: ConvoAIMessage = { role: "user", text: messageToSend };
        const updatedHistory = [...messages, userMsg];
        setMessages(updatedHistory);
        setInput("");
        setLoading(true);

        try {
            let reply = "I've received your message and I'm processing it.";
            if (onSend) reply = await onSend(messageToSend, updatedHistory);
            setMessages((prev) => [...prev, { role: "ai", text: reply }]);
        } catch {
            setMessages((prev) => [
                ...prev,
                { role: "ai", text: "Something went wrong. Please try again." },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) return null;

    return (
        <div className={`flex w-[320px] flex-col h-full bg-white dark:bg-gray-900 ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-gray-800 shrink-0">
                <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center">
                        <img src={ChatNeutral} alt="ChatNeutral" className="w-5 h-5" />
                    </div>
                    <span className="font-bold text-sm text-slate-800 dark:text-gray-100 tracking-tight">ConvoAI</span>
                </div>
                <button
                    onClick={onClose}
                    className="text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 p-1.5 rounded-md transition-colors"
                    aria-label="Close"
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
                <div className="bg-slate-50 dark:bg-gray-800 rounded-tl-sm rounded-tr-xl rounded-b-xl p-3 text-xs text-slate-500 dark:text-gray-400 leading-relaxed">
                    {greeting}
                </div>

                {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                            className={`max-w-[85%] px-3.5 py-2.5 text-xs leading-relaxed rounded-xl ${
                                msg.role === "user"
                                    ? "bg-violet-600 text-white rounded-tr-sm"
                                    : "bg-slate-50 dark:bg-gray-800 text-slate-700 dark:text-gray-300 rounded-tl-sm"
                            }`}
                        >
                            {msg.text}
                        </div>
                    </div>
                ))}

                {loading && (
                    <div className="flex gap-1.5 px-3 py-2">
                        {[0, 1, 2].map((i) => (
                            <span
                                key={i}
                                className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block"
                                style={{ animation: `dotBounce 1s ease-in-out ${i * 0.15}s infinite` }}
                            />
                        ))}
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {quickActions.length > 0 && (
                <div className="px-4 pb-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Ask ConvoAI</p>
                    <div className="flex flex-wrap gap-2">
                        {quickActions.map((action) => (
                            <button
                                key={action}
                                onClick={() => handleSend(action)}
                                className="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-300"
                            >
                                {action}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Input */}
            <div className="px-4 py-3 border-t border-slate-100 dark:border-gray-800 shrink-0">
                <div className="flex items-end gap-2 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl px-3 py-2 focus-within:border-violet-400 transition-colors">
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        rows={1}
                        className="flex-1 bg-transparent border-none outline-none resize-none text-xs text-slate-800 dark:text-gray-200 placeholder:text-slate-400 dark:placeholder:text-gray-500 leading-relaxed max-h-24 overflow-y-auto"
                    />
                    <div className="flex items-center gap-1.5 shrink-0 pb-0.5">
                        <button className="text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 p-1 transition-colors" aria-label="Voice">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <button className="text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 p-1 transition-colors" aria-label="Attach">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <button
                            onClick={() => handleSend()}
                            disabled={!input.trim() || loading}
                            className="bg-violet-600 disabled:bg-violet-300 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                        >
                            Send
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes dotBounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                }
            `}</style>
        </div>
    );
};

export default ConvoAIPanel;